import React from "react";
import { Navigate, useLocation } from "react-router";
import { Spinner } from "@fluentui/react-components";
import { useAuth } from "../../context/AuthContext";

type RequireAuthProps = {
  children: React.ReactElement;
  requiredPermission?: string;
  adminOnly?: boolean;
};

/**
 * Routing-level auth/RBAC guard.
 *
 * Hardening note (audit F-05/F-06, 30.04.2026): the previous build-time
 * bypass via `REACT_APP_DISABLE_ROLE_GUARD` and the implicit dev-mode skip
 * have been removed. The guard is now always active — Dev and Prod both
 * route auth+RBAC through the backend AuthzGuard + RBAC permissions. For
 * local development without an Entra/Entra-ID tenant, seed a demo user
 * (see backend `npm run seed` + `DEMO_USER_PASSWORD`).
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  requiredPermission,
  adminOnly,
}) => {
  const { isAuthenticated, isLoading, isAdmin, isGfgl, isPendingApproval, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "40vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spinner label="Berechtigung wird geprüft…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Pending users (banned=true) must land on /pending-approval, not
  // /access-denied — the latter implies "you don't have permission for this
  // route" while we actually want "your account is still being set up".
  // This must run before the adminOnly/requiredPermission checks.
  if (isPendingApproval) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (adminOnly && !(isAdmin || isGfgl)) {
    return <Navigate to="/access-denied" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};

export default RequireAuth;
